import {
  ApiOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SoundOutlined,
  SoundFilled,
} from "@ant-design/icons";
import { useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useEdgeState } from "../../../hooks/useEdgeMode";
import {
  EDGE_PREPARE_FEEDBACK,
  prepareThisDevice,
} from "../../../lib/edge/edgeSession";
import { logout, setAccess } from "../../../store/authSlice";
import { denyAccess } from "../../../api/access";
import { verifyPassword } from "../../../api/auth";
import LogoutConfirmModal from "../../ordering/components/LogoutConfirmModal";
import { useStationWebSocketContext } from "./StationWebSocketProvider";
import { useTranslation } from "react-i18next";
import ProfileDropdown from "../../../components/ProfileDropdown";
import BackButton from "../../../components/BackButton";
import type { MonitorInfoStation } from "../../../api/monitors";
import { useMenuStations } from "../../../hooks/useMenu";
import { getTranslation } from "../../../utils/translations";
import { useViewport } from "../../../hooks/useViewport";

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
};

interface NavbarProps {
  stations: MonitorInfoStation[];
  filterMode?: "unfulfilled" | "fulfilled";
  onFilterChange?: (mode: "unfulfilled" | "fulfilled") => void;
  // Шпикер (агрегиращ) монитор — показва бадж до заглавието.
  isShpicker?: boolean;
  // Разделен екран: имената на станциите се групират „лява | дясна", за да се
  // вижда коя половина какво слуша.
  splitScreen?: boolean;
  leftStationIds?: number[];
  rightStationIds?: number[];
}

export default function Navbar({
  stations: stationsFromProps,
  filterMode = "unfulfilled",
  onFilterChange,
  isShpicker = false,
  splitScreen = false,
  leftStationIds = [],
  rightStationIds = [],
}: NavbarProps) {
  const { t, i18n } = useTranslation();
  const access = useAppSelector((state) => state.auth.access);
  const username = useAppSelector((s) => s.auth.username);
  const names = useAppSelector((s) => s.auth.names);
  const displayName = names || username || "";
  const roles = useAppSelector((s) => s.auth.roles);
  const showBack = roles.length > 1;
  const { stations: wsStations } = useStationWebSocketContext();
  const menuStations = useMenuStations();
  const { isNarrow } = useViewport();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleRefreshAll = () => {
    wsStations.forEach((s) => s.refresh());
  };

  const handleToggleSoundAll = () => {
    wsStations.forEach((s) => s.enableSound());
  };

  const anySoundEnabled = wsStations.some((s) => s.soundEnabled);
  // Звукът е запомнен като включен, но чака първи клик, за да се отключи аудиото.
  const anySoundPending = wsStations.some((s) => s.soundPending);
  const hasWs = wsStations.length > 0;

  const { message } = App.useApp();

  // „Подготви това устройство" (docs/edge.md): кухненският екран е този, който
  // МЪЛЧИ — никой не го отваря, за да го провери, а разрешението за локалната
  // мрежа се дава само при действие на човек. Затова бутонът е и тук, не само в
  // Конфигурация → Устройства, където монтажникът е на друга машина.
  // Показва се само при включен резерв и в облачен режим: в локален режим
  // екранът вече работи срещу касата и няма какво да се подготвя.
  const edge = useEdgeState();
  const [preparing, setPreparing] = useState(false);
  const showPrepare = edge.enabled && edge.mode === "cloud";

  const handlePrepare = async () => {
    if (preparing) return;
    setPreparing(true);
    try {
      const { key, tone } = EDGE_PREPARE_FEEDBACK[await prepareThisDevice()];
      message[tone](t(key));
    } finally {
      setPreparing(false);
    }
  };

  // Монитор акаунтите са лични, само с модул „Монитори" (roles.length === 1) —
  // при тях „Изход" иска паролата на акаунта (както пълния изход от ПИН
  // екрана), за да не може персоналът случайно/самоволно да спре монитора.
  // Админ акаунт (с повече модули) излиза свободно, без парола.
  const requirePasswordOnLogout = roles.length === 1;
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const performLogout = async () => {
    await dispatch(logout());
    navigate("/login", { replace: true });
  };

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else if (requirePasswordOnLogout) {
      setLogoutConfirmOpen(true);
    } else {
      await performLogout();
    }
  };

  const handleLogoutConfirm = async (password: string) => {
    setLogoutLoading(true);
    try {
      const result = await verifyPassword(password);
      if (result.valid) {
        await performLogout();
      } else {
        message.error(t("logoutConfirm.invalidPassword"));
      }
    } catch {
      message.error(t("logoutConfirm.verificationError"));
    } finally {
      setLogoutLoading(false);
      setLogoutConfirmOpen(false);
    }
  };

  // Име на станция за подзаглавието: преводът от менюто, иначе името от
  // monitor info-то.
  const stationDisplayName = (stationId: number): string => {
    const menuStation = menuStations.find((s) => s.id === stationId);
    if (menuStation) {
      return getTranslation(
        menuStation.translations,
        i18n.language,
        menuStation.slug,
      );
    }
    return (
      stationsFromProps.find((s) => s.id === stationId)?.name ??
      `#${stationId}`
    );
  };

  // При разделен екран: „лява половина | дясна половина"; иначе — плоският
  // списък, както досега.
  const stationsText = splitScreen
    ? `${leftStationIds.map(stationDisplayName).join(" · ")}  |  ${rightStationIds
        .map(stationDisplayName)
        .join(" · ")}`
    : stationsFromProps.map((station) => stationDisplayName(station.id)).join(" · ");

  return (
    <header
      className="flex items-center justify-between flex-shrink-0"
      style={{
        padding: isNarrow ? "10px 16px" : "11px 28px",
        minHeight: isNarrow ? 48 : 60,
        borderBottom: `1px solid ${R.border}`,
        background: access
          ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
          : R.surface,
        gap: 10,
      }}
    >
      {/* Left — logo + title + meta */}
      <div
        className="flex items-center"
        style={{ gap: 10, minWidth: 0, flex: isNarrow ? "0 0 auto" : 1 }}
      >
        {showBack && <BackButton onClick={() => navigate("/")} />}
        <img
          src="/logo-ts.png"
          alt="UnrealSoft"
          onClick={() => (showBack ? navigate("/") : undefined)}
          style={{
            height: isNarrow ? 26 : 34,
            width: "auto",
            cursor: showBack ? "pointer" : "default",
            flexShrink: 0,
          }}
        />
        {!isNarrow && (
          <div style={{ minWidth: 0, lineHeight: 1.2 }}>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: -0.5,
                margin: 0,
                color: R.ink,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t("monitors.title")}
              {isShpicker && (
                <span className="ml-[10px] inline-flex translate-y-[-2px] items-center gap-[5px] rounded-full border border-brand-tint2 bg-brand-tint px-[10px] py-[3px] align-middle text-[11px] font-bold uppercase tracking-[0.8px] text-brand-deep-ink">
                  <span className="h-[6px] w-[6px] rounded-full bg-brand" />
                  {t("monitors.shpickerBadge")}
                </span>
              )}
            </h1>
            {stationsText && (
              <div
                style={{
                  fontSize: 11,
                  color: R.textMuted,
                  marginTop: 4,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 560,
                }}
              >
                {stationsText}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right — filter + actions */}
      <div
        className="flex items-center"
        style={{ gap: isNarrow ? 6 : 10, flexShrink: 0 }}
      >
        {/* Segmented filter */}
        <FilterSegmented
          value={filterMode}
          onChange={(v) => onFilterChange?.(v)}
          isNarrow={isNarrow}
          labels={{
            unfulfilled: t("monitors.unfulfilled"),
            fulfilled: t("monitors.fulfilled"),
          }}
        />

        {hasWs && (
          <>
            <IconButton
              title={t("monitors.refresh", "Refresh")}
              onClick={handleRefreshAll}
            >
              <ReloadOutlined style={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              title={
                anySoundEnabled
                  ? t("monitors.soundOff", "Sound Off")
                  : t("monitors.soundOn", "Sound On")
              }
              onClick={handleToggleSoundAll}
              active={anySoundEnabled}
              pending={anySoundPending}
            >
              {anySoundEnabled ? (
                <SoundFilled style={{ fontSize: 15 }} />
              ) : (
                <SoundOutlined style={{ fontSize: 15 }} />
              )}
            </IconButton>
          </>
        )}

        {showPrepare && (
          <IconButton
            // Иконата сама не казва нищо на кухненския екран — подсказката
            // носи и името на действието, и какво прави то.
            title={
              preparing
                ? t("edge.preparing")
                : `${t("edge.prepareDevice")} — ${t("edge.prepareDeviceHint")}`
            }
            onClick={handlePrepare}
            active={edge.edgeReachable === true}
          >
            {preparing ? (
              <LoadingOutlined style={{ fontSize: 15 }} />
            ) : (
              <ApiOutlined style={{ fontSize: 15 }} />
            )}
          </IconButton>
        )}

        <ProfileDropdown
          displayName={displayName}
          username={username ?? ""}
          access={access}
          isNarrow={isNarrow}
          onLogout={handleLogout}
        />
      </div>

      <LogoutConfirmModal
        open={logoutConfirmOpen}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogoutConfirm}
        loading={logoutLoading}
      />
    </header>
  );
}

function IconButton({
  children,
  onClick,
  title,
  active,
  pending,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  active?: boolean;
  // „Armed" състояние — звукът е запомнен вкл., но чака първи клик: същият
  // фон като активния, но сива иконка. Има приоритет над обичайното `active`.
  pending?: boolean;
}) {
  // Фон/цвят по състояние: pending → същия блед оранжев тинт като активния, но
  // сива иконка; active → блед оранжев тинт + оранжева иконка; иначе → прозрачно + сиво.
  const bg = pending || active ? R.brandTint : "transparent";
  const fg = pending ? R.textMuted : active ? R.brandDeep : R.textMuted;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        height: 30,
        width: 30,
        borderRadius: 6,
        background: bg,
        border: "none",
        color: fg,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 120ms, color 120ms",
      }}
      onMouseEnter={(e) => {
        if (active || pending) return;
        e.currentTarget.style.background = "var(--color-shade-1)";
        e.currentTarget.style.color = R.ink;
      }}
      onMouseLeave={(e) => {
        if (active || pending) {
          e.currentTarget.style.background = bg;
          e.currentTarget.style.color = fg;
          return;
        }
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = R.textMuted;
      }}
    >
      {children}
    </button>
  );
}

function FilterSegmented({
  value,
  onChange,
  isNarrow,
  labels,
}: {
  value: "unfulfilled" | "fulfilled";
  onChange: (v: "unfulfilled" | "fulfilled") => void;
  isNarrow: boolean;
  labels: { unfulfilled: string; fulfilled: string };
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        background: "var(--color-shade-1)",
        border: `1px solid ${R.border}`,
        borderRadius: 10,
        gap: 2,
      }}
    >
      <Seg
        selected={value === "unfulfilled"}
        onClick={() => onChange("unfulfilled")}
        isNarrow={isNarrow}
      >
        {labels.unfulfilled}
      </Seg>
      <Seg
        selected={value === "fulfilled"}
        onClick={() => onChange("fulfilled")}
        isNarrow={isNarrow}
      >
        {labels.fulfilled}
      </Seg>
    </div>
  );
}

function Seg({
  selected,
  onClick,
  children,
  isNarrow,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  isNarrow: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: isNarrow ? 26 : 28,
        padding: isNarrow ? "0 10px" : "0 14px",
        borderRadius: 8,
        border: "none",
        background: selected ? R.surface : "transparent",
        color: selected ? R.ink : R.textSoft,
        fontSize: isNarrow ? 11.5 : 12.5,
        fontWeight: 600,
        letterSpacing: -0.1,
        cursor: "pointer",
        transition: "background 120ms, color 120ms, box-shadow 120ms",
        boxShadow: selected
          ? "0 1px 2px rgba(17,17,17,0.05), 0 0 0 1px rgba(17,17,17,0.04)"
          : "none",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.color = R.ink;
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.color = R.textSoft;
      }}
    >
      {children}
    </button>
  );
}
